import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useUrlFilters, updateUrlFilters } from '../../utils/urlFilters'
import useKpiMeasures from '../../hooks/useKpiMeasures'
import Card from '../ui/Card'
import KpiCardGroup from '../ui/KpiCard/KpiCardGroup'
import ResourceScopePicker from '../Evolution/ResourceScopePicker'
import EvolutionChart from './EvolutionChart'
import ChartControls from './ChartControls'
import SectionsList from './SectionsList'
import SectionDrawer from './SectionDrawer'
import { useStatistics } from './hooks'
import {
  DEFAULT_PER_PAGE, DRAFT_GRANULARITY, GRANULARITY_OPTIONS, VIEW_MODES
} from './constants'

// Draft granularity only makes sense cross-drafts (no draftId) on a
// Recurrent collect: a spontaneous collect has no planification to group by.
const showsDraftGranularity = (draftId, recurrent) => !draftId && recurrent

const defaultGranularityFor = (draftId, recurrent) => {
  if (showsDraftGranularity(draftId, recurrent))
    return DRAFT_GRANULARITY.value
  return draftId ? 'week' : 'month'
}

const granularityOptionsFor = (draftId, recurrent) => {
  if (showsDraftGranularity(draftId, recurrent))
    return [...GRANULARITY_OPTIONS, DRAFT_GRANULARITY]
  return GRANULARITY_OPTIONS
}

const buildDateRange = (filters, dateRangeMin, dateRangeMax) => ({
  startDate: filters.start_date || dateRangeMin,
  endDate: filters.end_date || dateRangeMax
})

const resolveGranularity = (filterValue, options, fallback) => {
  const match = options.find(opt => opt.value === filterValue)
  return match?.value || fallback
}

const StatisticsContent = ({
  collectId, draftId, recurrent, hasGradeQuestions,
  hasAlertsConfigured, hasAdditionalAnswer, resourcesHref, gradeMax, dateRangeMin, dateRangeMax
}) => {
  const filters = useUrlFilters()
  const [selectedResourceIds, setSelectedResourceIds] = useState([])
  const granularityOptions = useMemo(
    () => granularityOptionsFor(draftId, recurrent), [draftId, recurrent]
  )
  const defaultGranularity = useMemo(
    () => defaultGranularityFor(draftId, recurrent), [draftId, recurrent]
  )
  const granularityArgs = [filters.granularity, granularityOptions, defaultGranularity]
  const granularity = useMemo(
    () => resolveGranularity(...granularityArgs), granularityArgs
  )
  const viewMode = filters.view === VIEW_MODES.SATISFACTION? VIEW_MODES.SATISFACTION: VIEW_MODES.GRADE
  const dateRange = useMemo(
    () => buildDateRange(filters, dateRangeMin, dateRangeMax), [filters, dateRangeMin, dateRangeMax]
  )
  const page = Math.max(1, parseInt(filters.page, 10) || 1)
  const perPage = Math.max(1, parseInt(filters.per_page, 10) || DEFAULT_PER_PAGE)
  const drawerSectionId = filters.drawer ? parseInt(filters.drawer, 10) : null

  const { data, overviewLoading, sectionsLoading, error } = useStatistics({
    collectId, draftId, granularity, dateRange, page, perPage, resourceIds: selectedResourceIds
  })

  const { values: kpiValues } = useKpiMeasures(collectId, draftId, {
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    resourceIds: selectedResourceIds
  })

  const drawerSection = useMemo(() => {
    if (!drawerSectionId)
      return null
    return data.sections.rows.find(row => row.id === drawerSectionId) || null
  }, [drawerSectionId, data.sections.rows])

  const isChartEmpty = useMemo(() => {
    const buckets = viewMode === VIEW_MODES.SATISFACTION? data.evolution.satisfaction: data.evolution.grade
    return (buckets || []).length === 0
  }, [viewMode, data.evolution])

  const handleGranularityChange = useCallback(value => {
    updateUrlFilters({ granularity: value === defaultGranularity ? null : value })
  }, [defaultGranularity])
  const handleViewModeChange = useCallback(value => {
    updateUrlFilters({ view: value === VIEW_MODES.GRADE ? null : value })
  }, [])
  const handlePageChange = useCallback(value => {
    updateUrlFilters({ page: value === 1 ? null : value })
  }, [])
  const handlePerPageChange = useCallback(value => {
    const newPerPage = value === DEFAULT_PER_PAGE ? null : value
    // eslint-disable-next-line camelcase
    updateUrlFilters({ per_page: newPerPage, page: null })
  }, [])
  const handleOpenSection = useCallback(sectionId => {
    updateUrlFilters({ drawer: sectionId })
  }, [])
  const handleCloseDrawer = useCallback(() => {
    updateUrlFilters({ drawer: null })
  }, [])

  if (error) {
    return (
      <div className="tw:p-4 tw:bg-red-50 tw:text-red-700 tw:rounded-md!">
        Une erreur est survenue lors du chargement.
      </div>
    )
  }

  return (
    <div>
      <KpiCardGroup
        alertsCount={kpiValues.alertsCount}
        className="tw:mb-6"
        controlsCount={kpiValues.controlsCount}
        grade={kpiValues.grade}
        gradeMax={gradeMax}
        hasAdditionalAnswer={hasAdditionalAnswer}
        hasAlertsConfigured={hasAlertsConfigured}
        satisfaction={kpiValues.satisfaction}
        showSatisfaction={hasGradeQuestions}
      />
      <div className="tw:mb-4">
        <Card
          title="Évolution de la note"
          headerRight={isChartEmpty && selectedResourceIds.length === 0 ? null : (
            <div className="tw:flex tw:items-center tw:gap-3">
              <ResourceScopePicker
                collectId={collectId}
                selectedResourceIds={selectedResourceIds}
                onChange={setSelectedResourceIds}
              />
              <ChartControls
                granularity={granularity}
                granularityOptions={granularityOptions}
                hasGradeQuestions={hasGradeQuestions}
                viewMode={viewMode}
                onGranularityChange={handleGranularityChange}
                onViewModeChange={handleViewModeChange}
              />
            </div>
          )}
          plainHeader
        >
          <EvolutionChart
            evolution={data.evolution}
            gradeMax={gradeMax}
            isLoading={overviewLoading}
            viewMode={viewMode}
          />
          <SectionsList
            gradeMax={gradeMax}
            loading={sectionsLoading}
            pagy={data.sections.pagy}
            perPage={perPage}
            rows={data.sections.rows}
            viewMode={viewMode}
            onOpenSection={handleOpenSection}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
          />
        </Card>
      </div>
      <SectionDrawer
        collectId={collectId}
        dateRange={dateRange}
        draftId={draftId}
        gradeMax={gradeMax}
        resourceIds={selectedResourceIds}
        resourcesHref={resourcesHref}
        section={drawerSection}
        sectionId={drawerSectionId}
        viewMode={viewMode}
        onClose={handleCloseDrawer}
      />
    </div>
  )
}

StatisticsContent.propTypes = {
  collectId: PropTypes.number.isRequired,
  dateRangeMax: PropTypes.string,
  dateRangeMin: PropTypes.string,
  draftId: PropTypes.number,
  gradeMax: PropTypes.number.isRequired,
  hasAdditionalAnswer: PropTypes.bool,
  hasAlertsConfigured: PropTypes.bool.isRequired,
  hasGradeQuestions: PropTypes.bool.isRequired,
  recurrent: PropTypes.bool,
  resourcesHref: PropTypes.string.isRequired
}

StatisticsContent.defaultProps = {
  dateRangeMax: null,
  dateRangeMin: null,
  draftId: null,
  hasAdditionalAnswer: false,
  recurrent: false
}

export default StatisticsContent
